/**
 * 用户类型判断 Hook
 * 用于区分租户管理员和租户普通用户，控制菜单显示
 *
 * 注意：本系统为纯租户端系统，不支持平台管理员登录
 * PLATFORM_ADMIN 类型仅保留用于类型完整性，实际不会出现
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { getStorageItem, STORAGE_KEYS } from '@/utils/storage';
import type { TenantType } from '@/components/ProductManagement';

/**
 * 用户类型枚举
 */
export enum UserType {
  PLATFORM_ADMIN = 'platform_admin',  // 平台管理员
  TENANT_ADMIN = 'tenant_admin',      // 租户管理员
  TENANT_USER = 'tenant_user',        // 租户普通用户
}

/**
 * 用户信息接口（扩展）
 */
export interface ExtendedUserInfo {
  id: number;
  username: string;
  nickname?: string;
  avatar?: string;
  tenant_id?: number;
  tenant_code?: string;
  tenant_name?: string;
  tenant_type?: TenantType;
  user_type?: UserType;
  user_scope?: string;
  /** 分销层级（fnd_tenants.distribution_level）：0=顶级租户（品牌商），1+=下级客户；旧缓存可能为 undefined */
  distribution_level?: number;
  roles?: Array<{ code: string; name: string }>;
  permissions?: string[];
}

/**
 * 从 localStorage 获取用户信息
 */
const getUserInfoFromStorage = (): ExtendedUserInfo | null => {
  try {
    const userInfo = getStorageItem<ExtendedUserInfo>(STORAGE_KEYS.USER_INFO);
    return userInfo || null;
  } catch {
    return null;
  }
};

/**
 * 根据用户信息推断用户类型
 * 规则：
 * - tenant_id 为空或为 0：平台管理员
 * - tenant_id > 0 且有 admin 角色或用户名为 admin：租户管理员
 * - tenant_id > 0 且无 admin 角色：租户普通用户
 */
const inferUserType = (userInfo: ExtendedUserInfo | null): UserType => {
  if (!userInfo) {
    return UserType.TENANT_USER; // 默认
  }

  // 如果后端返回了 user_type，直接使用
  if (userInfo.user_type) {
    return userInfo.user_type;
  }

  // 根据 tenant_id 判断
  const tenantId = userInfo.tenant_id;

  // 平台管理员：没有租户ID或租户ID为0
  if (!tenantId || tenantId === 0) {
    return UserType.PLATFORM_ADMIN;
  }

  // 租户用户：检查是否有管理员角色或用户名为 admin
  const roles = userInfo.roles || [];
  const isAdmin = roles.some(role =>
    role.code === 'admin' ||
    role.code === 'tenant_admin' ||
    role.code === 'super_admin'
  );

  // 如果用户名是 admin，也视为租户管理员（兼容角色信息未配置的情况）
  const isAdminUser = userInfo.username === 'admin';

  return (isAdmin || isAdminUser) ? UserType.TENANT_ADMIN : UserType.TENANT_USER;
};

/**
 * 根据租户ID推断租户类型
 */
const inferTenantType = (tenantId?: number): TenantType | null => {
  if (!tenantId) return null;

  // 租户ID到类型的映射（根据实际情况配置）
  const tenantTypeMap: Record<number, TenantType> = {
    1: 'brand_tenant',    // 租户1是品牌租户
    2: 'belle',     // 租户2是百丽
    3: 'bosideng',  // 租户3是波司登
  };

  return tenantTypeMap[tenantId] || null;
};

/**
 * 判断是否为顶级租户（品牌商，distribution_level === 0）
 * - 排除平台用户（platform 用户 tenant_id=0、level 兜底 0，会误判）
 * - 旧缓存兼容：distribution_level === undefined（存量 localStorage user_info）时视为未知，返回 false，重新登录后生效
 */
export const isTopLevelTenant = (userInfo: ExtendedUserInfo | null): boolean => {
  if (!userInfo) return false;
  return userInfo.user_scope !== 'platform' && userInfo.distribution_level === 0;
};

/**
 * 获取用户类型对应的默认跳转路径
 * 纯租户系统，所有用户统一跳转到租户工作台
 */
export const getDefaultRedirectPath = (_userType: UserType): string => {
  return '/tenant/dashboard';
};

/**
 * useUserType Hook
 * 获取当前用户的类型信息
 */
export const useUserType = () => {
  const [userInfo, setUserInfo] = useState<ExtendedUserInfo | null>(null);
  const [loading, setLoading] = useState(true);

  /**
   * 刷新用户信息
   */
  const refresh = useCallback(() => {
    setLoading(true);
    const info = getUserInfoFromStorage();
    setUserInfo(info);
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();

    // 监听 storage 变化
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === STORAGE_KEYS.USER_INFO) {
        refresh();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [refresh]);

  /**
   * 用户类型
   */
  const userType = useMemo(() => inferUserType(userInfo), [userInfo]);

  /**
   * 租户类型
   */
  const tenantType = useMemo((): TenantType | null => {
    if (!userInfo) return null;
    return userInfo.tenant_type || inferTenantType(userInfo.tenant_id);
  }, [userInfo]);

  /**
   * 是否是平台管理员
   */
  const isPlatformAdmin = useMemo(() => userType === UserType.PLATFORM_ADMIN, [userType]);

  /**
   * 是否是租户管理员
   */
  const isTenantAdmin = useMemo(() => userType === UserType.TENANT_ADMIN, [userType]);

  /**
   * 是否是租户用户（包括管理员和普通用户）
   */
  const isTenantUser = useMemo(() =>
    userType === UserType.TENANT_ADMIN || userType === UserType.TENANT_USER,
    [userType]
  );

  /**
   * 是否是品牌租户
   */
  const isBrandTenant = useMemo(() => tenantType === 'brand_tenant', [tenantType]);

  /**
   * @deprecated Use hasFeature('belle_*') instead
   * 是否是百丽租户
   */
  const isBelle = useMemo(() => tenantType === 'belle', [tenantType]);

  /**
   * 是否是波司登租户
   */
  const isBosideng = useMemo(() => tenantType === 'bosideng', [tenantType]);

  /**
   * 是否是顶级租户（品牌商，distribution_level === 0，排除平台用户）
   */
  const isTopLevel = useMemo(() => isTopLevelTenant(userInfo), [userInfo]);

  /**
   * 租户名称（来自后端返回）
   */
  const tenantName = useMemo(() => userInfo?.tenant_name || null, [userInfo]);

  /**
   * 默认跳转路径
   */
  const defaultRedirectPath = useMemo(() => getDefaultRedirectPath(userType), [userType]);

  /**
   * 检查是否有访问某个路径的权限
   * 纯租户系统，所有租户可访问相同路径
   */
  const canAccessPath = useCallback((_path: string): boolean => {
    return true;
  }, []);

  return {
    // 用户信息
    userInfo,
    loading,
    refresh,

    // 用户类型
    userType,
    tenantType,
    tenantName,

    // 类型判断
    isPlatformAdmin,
    isTenantAdmin,
    isTenantUser,

    // 租户判断
    isBrandTenant,
    isBelle,
    isBosideng,
    isTopLevelTenant: isTopLevel,

    // 路由相关
    defaultRedirectPath,
    canAccessPath,
  };
};

export default useUserType;
